import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { BellRing, Save } from "lucide-react";
import { toast } from "sonner";

// Per-proposal reminder control: turn automatic reminders on/off for this one proposal,
// and optionally override the site-wide frequency with a custom schedule.
export default function ProposalReminderCard({ proposalId, canEdit }) {
  const [enabled, setEnabled] = useState(true);
  const [mode, setMode] = useState("default");
  const [items, setItems] = useState([]);
  const [siteItems, setSiteItems] = useState([]);
  const [nextAt, setNextAt] = useState(null);
  const [pausedUntil, setPausedUntil] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/proposals/${proposalId}/reminder-settings`).then((r) => {
    setEnabled(r.data.enabled);
    setMode(r.data.mode);
    setItems(r.data.items || []);
    setSiteItems(r.data.site_items || []);
    setNextAt(r.data.next_reminder_at || null);
    setPausedUntil(r.data.paused_until || null);
  }).catch(() => {});
  useEffect(() => { load(); }, [proposalId]);

  const upd = (n, patch) => setItems((xs) => xs.map((x) => (x.n === n ? { ...x, ...patch } : x)));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/proposals/${proposalId}/reminder-settings`,
        { enabled, mode, items: mode === "custom" ? items : undefined });
      setEnabled(data.enabled); setMode(data.mode); setItems(data.items || []);
      setNextAt(data.next_reminder_at || null); setPausedUntil(data.paused_until || null);
      toast.success("Reminder settings saved");
    } catch { toast.error("Could not save reminder settings"); }
    finally { setSaving(false); }
  };

  const fmtNice = (iso) => { if (!iso) return ""; const d = new Date(iso); return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); };
  const pausedInFuture = pausedUntil && new Date(pausedUntil) > new Date();
  const nextIsPast = nextAt && new Date(nextAt) < new Date(new Date().toDateString());
  const sanityWarn = mode === "custom" && (() => {
    const en = items.filter((x) => x.enabled);
    for (let i = 1; i < en.length; i++) if (en[i].days <= en[i - 1].days) return true;
    return false;
  })();

  const rows = mode === "custom" ? items : siteItems;
  const rowsEditable = canEdit && enabled && mode === "custom";

  return (
    <Card className="p-6 rounded-md space-y-4" data-testid="proposal-reminder-card">
      <div className="flex items-center gap-2">
        <BellRing className="w-4 h-4 text-cass-teal" />
        <h3 className="font-alt font-semibold text-cass-navy">Automatic reminders</h3>
      </div>

      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm text-cass-navy font-medium">Send reminders for this proposal</div>
          <p className="text-xs text-cass-slate mt-0.5">Chaser emails go to anyone who hasn't signed. They stop once the proposal is accepted, rejected, expired or withdrawn.</p>
        </div>
        <Switch checked={enabled} disabled={!canEdit} onCheckedChange={(v) => setEnabled(!!v)} data-testid="proposal-reminders-enabled" />
      </div>

      <div className="text-xs -mt-1" data-testid="proposal-reminder-next">
        {!enabled ? <span className="text-amber-600">Reminders are off — no automatic nudges.</span>
          : pausedInFuture ? <span className="text-amber-600">Paused until <b>{fmtNice(pausedUntil)}</b> — reminders resume then.</span>
            : nextAt ? <span className="text-cass-slate">Next reminder: <b className="text-cass-navy">{nextIsPast ? "due on the next daily run" : fmtNice(nextAt)}</b></span>
              : <span className="text-cass-slate">No further reminders scheduled.</span>}
      </div>

      {enabled && (
        <div className="space-y-3">
          <div className="flex gap-2" data-testid="proposal-reminder-mode">
            <button type="button" disabled={!canEdit} onClick={() => setMode("default")}
              className={`flex-1 text-xs font-medium rounded-md border px-3 py-2 transition-colors ${mode === "default" ? "border-cass-teal bg-cass-teal/10 text-cass-navy" : "border-border text-cass-slate hover:border-cass-teal/50"}`}
              data-testid="proposal-reminder-mode-default">Use site-wide schedule</button>
            <button type="button" disabled={!canEdit} onClick={() => setMode("custom")}
              className={`flex-1 text-xs font-medium rounded-md border px-3 py-2 transition-colors ${mode === "custom" ? "border-cass-teal bg-cass-teal/10 text-cass-navy" : "border-border text-cass-slate hover:border-cass-teal/50"}`}
              data-testid="proposal-reminder-mode-custom">Custom for this proposal</button>
          </div>

          <p className="text-xs text-cass-slate">
            {mode === "default"
              ? "This proposal follows the firm's default reminder schedule (shown below)."
              : "Set how many days after the proposal was sent each reminder goes out."}
          </p>

          <div className="space-y-2">
            {rows.map((it) => (
              <div key={it.n} className="flex items-center gap-3 border border-border rounded-md px-3 py-2" data-testid={`proposal-reminder-row-${it.n}`}>
                <span className="grid place-items-center w-6 h-6 rounded-full bg-cass-teal text-white text-xs font-semibold shrink-0">{it.n}</span>
                <div className="flex items-center gap-2">
                  <Input type="number" min={1} className="w-20 h-8" value={it.days} disabled={!rowsEditable}
                    onChange={(e) => upd(it.n, { days: Math.max(1, Number(e.target.value) || 1) })} data-testid={`proposal-reminder-days-${it.n}`} />
                  <span className="text-xs text-cass-slate">days after sent</span>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <Switch checked={it.enabled} disabled={!rowsEditable} onCheckedChange={(v) => upd(it.n, { enabled: !!v })} data-testid={`proposal-reminder-enabled-${it.n}`} />
                  <span className="text-xs text-cass-slate w-8">{it.enabled ? "On" : "Off"}</span>
                </div>
              </div>
            ))}
          </div>
          {sanityWarn && <p className="text-xs text-amber-600" data-testid="proposal-reminder-sanity">Heads up: each reminder should be a larger number of days than the one before it, otherwise later reminders won't go out in order.</p>}
        </div>
      )}

      {!enabled && <p className="text-xs text-amber-600" data-testid="proposal-reminders-off-note">Automatic reminders are off for this proposal. You can still resend the link manually.</p>}

      {canEdit && <Button className="bg-cass-navy text-white" onClick={save} disabled={saving} data-testid="proposal-reminder-save"><Save className="w-4 h-4 mr-1" />{saving ? "Saving…" : "Save reminder settings"}</Button>}
    </Card>
  );
}
